/**
 * 模块 3 Agent SSE 客户端
 * 接收 Hono 流式响应,解析 "event: <type>\ndata: <json>" 格式
 */
import { useAuth } from './store'

export type AgentEvent =
  | { type: 'progress'; step: number; total: number; message: string }
  | { type: 'tool'; tool: string; args: unknown }
  | { type: 'token'; text: string }
  | { type: 'report'; report: unknown }
  | { type: 'done' }
  | { type: 'error'; error: string }

export async function* streamAgent(formInput: unknown): AsyncGenerator<AgentEvent> {
  // 与 api.ts 的 call() 一致:从 store 取 token 加 Bearer
  const token = useAuth.getState().token ?? localStorage.getItem('gaokao_token')
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (token) headers['Authorization'] = `Bearer ${token}`

  // 流式 agent 端点(cloudFunctions Hono streamSSE,见 cloud-functions/routes/agent/run.ts)
  // 注:Accept-Encoding: identity 由 public/sw.js 拦截覆盖(浏览器禁了 JS 直接设这个头)
  const res = await fetch('/api/agent/stream', {
    method: 'POST',
    headers,
    body: JSON.stringify(formInput),
  })
  if (!res.ok || !res.body) {
    throw new Error(`agent stream failed: ${res.status}`)
  }

  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let buf = ''

  while (true) {
    const { value, done } = await reader.read()
    if (done) break
    buf += decoder.decode(value, { stream: true })

    // SSE 事件以 \n\n 分隔
    let idx: number
    while ((idx = buf.indexOf('\n\n')) >= 0) {
      const raw = buf.slice(0, idx)
      buf = buf.slice(idx + 2)
      const event = parseSse(raw)
      if (event) {
        yield event
        // 关键:让 React 在每个 token 之间 commit(否则一个 chunk 内所有 token 合成 1 次 render)
        await Promise.resolve()
      }
    }
  }
}

function parseSse(raw: string): AgentEvent | null {
  const lines = raw.split('\n')
  let data: string | null = null
  let event: string | null = null
  for (const line of lines) {
    if (line.startsWith('data:')) data = line.slice(5).trim()
    else if (line.startsWith('event:')) event = line.slice(6).trim()
  }
  if (!data) return null
  try {
    const obj = JSON.parse(data) as Record<string, unknown>
    // 后端 SSE event 字段携带 type,data 里没有 — 补上以匹配 AgentEvent 类型
    if (event && typeof obj.type !== 'string') (obj as { type: string }).type = event
    return obj as unknown as AgentEvent
  } catch {
    return null
  }
}
